import React, { useEffect, useMemo, useState } from 'react';
import { Clock, Utensils, CheckCircle } from 'lucide-react';
import './WaiterPickupQueue.css';
import { undoLastDelivery, canUndo, undoLastDeliveryForTable, canUndoForTable, undoLastDeliveryForTicket, canUndoForTicket } from './SharedPickupStore.js';

// Integrated DeliverItemsModal component
const DeliverItemsModal = ({ order, onClose, onConfirm, onUndoTable }) => {
  const [selected, setSelected] = useState(() => new Map()); // Map: itemId -> quantity to serve
  const [partialQty, setPartialQty] = useState(() => new Map()); // Map: itemId -> input quantity
  const grouped = useMemo(() => {
    const items = order?.items || [];
    return {
      // Keep grouping internals but we'll NOT display any status in UI per Zero-Clue Rule
      // Include items that are ready_on_pass, ready, pending, or null (all deliverable items)
      readyOnPass: items.filter(i => 
        i.status === 'ready_on_pass' || 
        i.status === 'ready' || 
        i.status === 'pending' || 
        i.status === null || 
        !i.status
      ),
      inHand: items.filter(i => i.status === 'in_hand'),
      inKitchen: items.filter(i => i.status === 'in_kitchen'),
      served: items.filter(i => i.status === 'served')
    };
  }, [order]);

  // Zero-Clue Rule: flatten to a single neutral list (no status hints)
  const available = useMemo(() => [...grouped.readyOnPass, ...grouped.inHand], [grouped]);

  // Live count of items selected with qty > 0
  const positiveSelected = useMemo(() => {
    return Array.from(selected.values()).filter(q => q > 0).length;
  }, [selected]);

  // Preselect all items with quantity 0 on open; no deselection allowed
  useEffect(() => {
    const sel = new Map();
    const par = new Map();
    available.forEach(item => {
      sel.set(item.id, 0);
      par.set(item.id, 0);
    });
    setSelected(sel);
    setPartialQty(par);
  }, [available]);

  const toggle = (id, maxQty) => {
    const newSelected = new Map(selected);
    const newPartial = new Map(partialQty);
    
    if (newSelected.has(id)) {
      newSelected.delete(id);
      newPartial.delete(id);
    } else {
      // Start from 0 when selecting item; user can increase with plus
      newSelected.set(id, 0);
      newPartial.set(id, 0);
    }
    setSelected(newSelected);
    setPartialQty(newPartial);
  };

  const updateQuantity = (id, qty, maxQty) => {
    // Allow empty string for user to clear and type new value
    if (qty === '' || qty === null || qty === undefined) {
      const newPartial = new Map(partialQty);
      newPartial.set(id, '');
      setPartialQty(newPartial);
      return;
    }
    
    let n = parseInt(qty);
    if (isNaN(n)) n = 0;
    const validQty = Math.max(0, Math.min(n, maxQty));
    const newSelected = new Map(selected);
    const newPartial = new Map(partialQty);
    
    newSelected.set(id, validQty);
    newPartial.set(id, validQty);
    setSelected(newSelected);
    setPartialQty(newPartial);
  };

  const markAll = () => {
    const newSelected = new Map();
    const newPartial = new Map();
    
    available.forEach(item => {
      newSelected.set(item.id, item.quantity);
      newPartial.set(item.id, item.quantity);
    });
    
    setSelected(newSelected);
    setPartialQty(newPartial);
  };

  // Single confirmation: confirm immediately without second modal
  const confirm = () => {
    const selectedItems = Array.from(selected.entries())
      .filter(([_, qty]) => qty > 0)
      .map(([id, qty]) => {
        // Find the actual item to get portion_id
        const item = available.find(i => i.id === id);
        return { 
          id: item?.portion_id || id, 
          portion_id: item?.portion_id || id,
          quantity: qty 
        };
      });
    
    if (selectedItems.length === 0) return;
    onConfirm(selectedItems);
  };

  return (
    <>
    <div className="wpq-deliver-modal-backdrop" onClick={onClose}>
      <div className="wpq-deliver-modal-container" onClick={e => e.stopPropagation()}>
        <div className="wpq-deliver-modal-header" style={{padding:'10px 0 12px 0'}}>
          <h3 className="wpq-deliver-modal-title" style={{textTransform:'uppercase', color:"white"}}>
            {order?.tableNumber != null ? `Table ${order.tableNumber}` : 'Deliver Items'}
          </h3>
          <div style={{display:'flex', gap:'8px', alignItems:'center', flexWrap:'nowrap'}}>
            {order?.tableNumber != null && canUndoForTable(order.tableNumber) && (
              <button
                onClick={() => {
                  // Parent may maintain history; prefer parent when provided
                  if (onUndoTable) {
                    onUndoTable(order.tableNumber);
                  } else {
                    undoLastDeliveryForTable(order.tableNumber);
                  }
                }}
                className="wpq-deliver-undo-btn"
                style={{padding:'6px 10px'}}
              >
                Undo Items
              </button>
            )}
            <div className="wpq-deliver-selected-count" style={{whiteSpace:'nowrap'}}>
              <span style={{fontWeight:700}}>{positiveSelected}</span> item{positiveSelected!==1?'s':''} selected
            </div>
            <button 
              onClick={markAll} 
              className="wpq-deliver-markall-btn"
              style={{
                padding:'6px 12px', 
                fontSize:'12px',
                fontWeight:'600',
                backgroundColor:'#f9c806',
                color:'#111',
                border:'none',
                borderRadius:'6px',
                cursor:'pointer',
                whiteSpace:'nowrap'
              }}
            >
              Mark All
            </button>
          </div>
        </div>
        {/* Subtitle swapped to show action */}
        <div style={{color:'white', fontSize:13, textAlign:'left', padding:'0 0 10px 0'}}>Deliver Items</div>
        <div className="wpq-deliver-modal-body">
          <p style={{color:'#9ca3af',textAlign:'left', margin:'0 0 0 0'}}>Select the items you are physically placing on the table</p>

          {available.length === 0 ? (
            <div className="ab-status-box" style={{textAlign:'center'}}>
              No items available to deliver
            </div>
          ) : (
            <div className="wpov-deliver-list">
              {available.map(item => (
                <div 
                  key={item.id} 
                  className={`wpov-deliver-row selected`}
                  style={{cursor: 'default', gridTemplateColumns: '1fr auto'}}
                >
                  <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                    <div className="wpov-deliver-name">{item.name || item.item_name}</div>
                    {/* Display customizations if present */}
                    {item.customizations && Array.isArray(item.customizations) && item.customizations.length > 0 && (
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '4px',
                        marginTop: '4px'
                      }}>
                        {item.customizations.map((customization, custIdx) => (
                          <span key={custIdx} style={{
                            background: '#2d2d2d',
                            color: '#fbbf24',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: '500',
                            border: '1px solid #3f3f3f'
                          }}>
                            {customization}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="wpov-deliver-qty-controls">
                    <div style={{display:'flex', alignItems:'center', gap:'6px'}}>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            const currentQty = partialQty.get(item.id) ?? 0;
                            if (currentQty > 0) {
                              updateQuantity(item.id, currentQty - 1, item.quantity);
                            }
                          }}
                          disabled={(partialQty.get(item.id) ?? 0) <= 0}
                          style={{
                            width:'24px',
                            height:'24px',
                            borderRadius:'4px',
                            border:'1px solid #374151',
                            backgroundColor: ((partialQty.get(item.id) ?? 0) <= 0) ? '#333' : '#1a1a1a',
                            color: ((partialQty.get(item.id) ?? 0) <= 0) ? '#666' : '#fff',
                            fontSize:'16px',
                            fontWeight:'bold',
                            cursor: ((partialQty.get(item.id) ?? 0) <= 0) ? 'not-allowed' : 'pointer',
                            display:'flex',
                            alignItems:'center',
                            justifyContent:'center',
                            transition:'all 0.2s'
                          }}
                        >
                          -
                        </button>
                        <input 
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={partialQty.get(item.id) ?? ''}
                          onClick={(e)=>e.stopPropagation()}
                          onChange={(e)=>{
                            const val = e.target.value;
                            // Allow only numbers
                            if (val === '' || /^[0-9]+$/.test(val)) {
                              updateQuantity(item.id, val, item.quantity);
                            }
                          }}
                          onBlur={(e)=>{
                            // On blur, ensure we have a valid number
                            const val = e.target.value;
                            if (val === '' || parseInt(val) === 0) {
                              updateQuantity(item.id, 0, item.quantity);
                            }
                          }}
                          style={{
                            width:'48px',
                            background:'#000000',
                            border:'none',
                            color:'#fff',
                            borderRadius:'4px',
                            padding:'2px 4px',
                            textAlign:'center',
                            fontWeight:600
                          }}
                        />
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            const currentQty = partialQty.get(item.id) ?? 0;
                            if (currentQty < item.quantity) {
                              updateQuantity(item.id, currentQty + 1, item.quantity);
                            }
                          }}
                          disabled={(partialQty.get(item.id) ?? 0) >= item.quantity}
                          style={{
                            width:'24px',
                            height:'24px',
                            borderRadius:'4px',
                            border:'1px solid #374151',
                            backgroundColor: ((partialQty.get(item.id) ?? 0) >= item.quantity) ? '#333' : '#1a1a1a',
                            color: ((partialQty.get(item.id) ?? 0) >= item.quantity) ? '#666' : '#fff',
                            fontSize:'16px',
                            fontWeight:'bold',
                            cursor: ((partialQty.get(item.id) ?? 0) >= item.quantity) ? 'not-allowed' : 'pointer',
                            display:'flex',
                            alignItems:'center',
                            justifyContent:'center',
                            transition:'all 0.2s'
                          }}
                        >
                          +
                        </button>
                        <span style={{color:'#666', fontSize:'12px', fontWeight:'400'}}>/{item.quantity}</span>
                      </div>
                  </div>
                  {/* Zero-Clue Rule: No status pill shown */}
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Footer bar: actions only (count moved beside Mark All) */}
        <div className="wpq-deliver-modal-footer">
          <div className="wpq-deliver-modal-actions" style={{margin:0, justifyContent:'center'}}>
            <button onClick={onClose} className="wpq-deliver-cancel-btn">Cancel</button>
            <button 
              onClick={confirm} 
              disabled={Array.from(selected.values()).every(q => q <= 0)} 
              className="wpq-deliver-confirm-btn"
              style={{whiteSpace:'nowrap'}}
            >
              Confirm Serve
            </button>
          </div>
        </div>
      </div>
    </div>
    {/* No second confirmation modal – single-step confirm */}
    </>
  );
};

export const PickupQueue = ({ items, onDeliveryItems, onAcknowledgePickup, onConfirmDelivered, onDeliverSelected, businessId, waiterAssignments, selectedWaiter, waiterDashboardEnabled }) => {
  const [deliveredItems, setDeliveredItems] = React.useState([]);
  const [removingItems, setRemovingItems] = React.useState(new Set());
  const [deliverOrder, setDeliverOrder] = React.useState(null);
  const [showHistory, setShowHistory] = React.useState(false);
  const [servedHistory, setServedHistory] = React.useState([]); // {orderId, orderNumber, tableNumber, servedAt, items:[{id,name,quantity}]}
  const [nowTick, setNowTick] = React.useState(Date.now()); // for live elapsed timers
  const [loadingHistory, setLoadingHistory] = React.useState(false);
  const [historyError, setHistoryError] = React.useState(null);
  

  
  // ✅ NEW: Fetch order history from backend when component mounts or waiter changes
  React.useEffect(() => {
    const fetchOrderHistory = async () => {
      // Only fetch if we have the necessary data
      if (!businessId || !selectedWaiter || !waiterAssignments || waiterAssignments.length === 0) {
        return;
      }

      const assignment = waiterAssignments.find(w => w.waiterName === selectedWaiter);
      if (!assignment) {
        return;
      }

      setLoadingHistory(true);
      setHistoryError(null);

      try {
        const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
        const url = `${API_BASE}/waiter/order-history?businessId=${businessId}&fromTable=${assignment.fromTable}&toTable=${assignment.toTable}`;
        

        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'X-Business-ID': businessId
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch order history: ${response.statusText}`);
        }

        const data = await response.json();
        // Transform backend data into servedHistory format
        const historyEntries = [];
        if (data.ordersByTable && Array.isArray(data.ordersByTable)) {
          data.ordersByTable.forEach(tableData => {
            if (tableData.orders && Array.isArray(tableData.orders)) {
              tableData.orders.forEach(order => {
                // Only include orders that have been served (have items with served status)
                if (order.items && order.items.length > 0) {
                  const transformedItems = order.items.map(item => ({
                    id: item.itemId || item.orderItemId,
                    name: item.name || item.itemName || 'Unknown Item',
                    quantity: item.quantity
                  }));
                  
                  historyEntries.push({
                    orderId: order.orderId,
                    orderNumber: order.orderNumber,
                    tableNumber: tableData.tableNumber,
                    servedAt: order.servedAt || order.orderTime || order.placedAt || new Date(),
                    items: transformedItems,
                    _k: `${order.orderId}-${new Date(order.servedAt || order.orderTime || order.placedAt).getTime()}`
                  });
                }
              });
            }
          });
        }

        setServedHistory(historyEntries);
        setLoadingHistory(false);

      } catch (err) {
        console.error('❌ [PickupQueue] Error fetching order history:', err);
        setHistoryError(err.message);
        setLoadingHistory(false);
      }
    };

    fetchOrderHistory();
  }, [businessId, selectedWaiter, waiterAssignments]); // Re-fetch when these change
  
  // Tick every 30s to refresh elapsed timers without heavy updates
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  // Helper: get elapsed minutes since order placed; fallback to when it became ready
  const getElapsedMinutes = (order) => {
    const placedAt = order.placedAt || order.createdAt; // preferred
    let baseTime = placedAt instanceof Date ? placedAt : (order.completedAt instanceof Date ? order.completedAt : null);
    
    // If baseTime is a string (IST timestamp from backend), parse it
    if (typeof baseTime === 'string') {
      baseTime = new Date(baseTime);
    }
    
    if (!baseTime || isNaN(baseTime.getTime())) return 0;
    
    // ✅ FIX: Backend sends IST time, but browser Date.now() is in local time (IST for India)
    // Since both are in IST timezone, direct comparison works correctly
    // The timestamp string is parsed as local time by JavaScript
    const now = Date.now();
    const elapsedMs = now - baseTime.getTime();
    return Math.floor(elapsedMs / 60000);
  };

  const formatElapsedShort = (mins) => {
    if (mins < 60) return `${mins}m`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h ${m}m`;
  };

  const getKitchenClass = (section) => {
    switch (section) {
      case 'Grill':
        return 'wpov-pq-kitchen grill';
      case 'Cold Prep':
        return 'wpov-pq-kitchen cold';
      case 'Hot Kitchen':
        return 'wpov-pq-kitchen hot';
      default:
        return 'wpov-pq-kitchen default';
    }
  };

  const handleConfirmDelivered = (itemId) => {
    // Add to delivered items (turns ash)
    const item = onDeliveryItems.find(i => i.id === itemId);
    if (item) {
      setDeliveredItems(prev => [...prev, { ...item, deliveredAt: new Date() }]);
      // Remove from view after animation
      setTimeout(() => {
        setDeliveredItems(prev => prev.filter(i => i.id !== itemId));
      }, 1500);
    }
    // Call parent handler
    onConfirmDelivered(itemId);
  };

  // Combine all items with their states
  // Filter out items from 'items' that are in 'onDeliveryItems' to prevent duplicates
  const onDeliveryIds = new Set(onDeliveryItems.map(i => i.id));
  const readyItems = items.filter(i => !onDeliveryIds.has(i.id));
  
  const allItems = [
    ...readyItems.map(i => ({ ...i, itemState: 'ready' })),
    ...onDeliveryItems.map(i => ({ ...i, itemState: 'on-delivery' })),
    ...deliveredItems.map(i => ({ ...i, itemState: 'delivered' }))
  ];

  const readyCount = readyItems.length;
  const deliveryCount = onDeliveryItems.length;

  const pendingItems = useMemo(() => {
    // Only show tasks that still need waiter action (exclude short-lived delivered animation entries)
    const list = [
      ...readyItems.map(i => ({ ...i, itemState: 'ready' })),
      ...onDeliveryItems.map(i => ({ ...i, itemState: 'on-delivery' }))
    ];
    // Filter out cards where readyOnPass is 0
    const filtered = list.filter(order => {
      const readyOnPass = order.readyQuantity || 0;
      return readyOnPass > 0;
    });
    // Sort by time elapsed since placed (oldest first). Fallback to completedAt.
    return filtered.sort((a, b) => getElapsedMinutes(b) - getElapsedMinutes(a));
  }, [readyItems, onDeliveryItems, nowTick]);

  return (
    <div className="wpov-pq-root">
      {/* Hidden toggle for parent to trigger */}
      <div className="wpov-pq-header-actions" style={{display: 'none'}}>
        <button
          className="wpov-history-toggle"
          onClick={() => setShowHistory(v => !v)}
        >
          {showHistory ? 'Hide History' : 'Order History'}
        </button>
        <Utensils className="wpov-pq-icon wpov-pq-gold" />
      </div>
      <div className="wpov-pq-card">
        <h2 className="wpov-pq-title">
          <div style={{display:'flex', gap:8, alignItems:'center', flexWrap:'nowrap'}}>
            <Utensils size={20} color="#ffffff" />
            <span style={{whiteSpace:'nowrap'}}>Dine-in Orders ({pendingItems.length})</span>
          </div>
          <div style={{display:'flex', gap:8, alignItems:'center'}}>
            {canUndo() && (
              <button
                className="wpov-history-toggle"
                onClick={() => {
                  if (servedHistory.length > 0) {
                    setServedHistory(prev => prev.slice(0, -1));
                  }
                  undoLastDelivery();
                }}
              >
                Undo Last
              </button>
            )}
          </div>
        </h2>
        {!waiterDashboardEnabled && (
          <div className="wd-toggle-warning-banner" style={{marginBottom: '12px', marginTop: '8px'}}>
            <span style={{whiteSpace:'nowrap'}}>switch on in owner dashboard to display the order</span>
          </div>
        )}
        {!showHistory && pendingItems.length === 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 'calc(100vh - 300px)',
            textAlign: 'center',
            color: '#9ca3af'
          }}>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px', color: '#f3f4f6' }}>
              No dine-in orders
            </h3>
            <p style={{ fontSize: '14px' }}>
              Dine-in items will appear here when ordered
            </p>
          </div>
        ) : !showHistory ? (
          <div className="wpov-pq-list">
            {pendingItems.map(order => {
              const elapsed = getElapsedMinutes(order);
              const isUrgent = elapsed >= 20; // highlight older tasks
              
              // Check if this card is being removed
              const isRemoving = removingItems.has(order.id);

              // Use backend-calculated quantities for accurate counts
              const readyOnPass = order.readyQuantity || 0;
              const served = order.servedQuantity || 0;
              
              // per-order summary like waiter project
              const items = order.items || [];
              // No need to filter - backend already groups and aggregates by status

              let cardClass = 'wpov-pq-item';
              if (isRemoving) {
                cardClass += ' card-removing';
              } else if (order.itemState === 'ready') {
                cardClass += isUrgent ? ' urgent' : '';
              } else if (order.itemState === 'on-delivery') {
                cardClass += ' wpov-pq-on-delivery';
              } else if (order.itemState === 'delivered') {
                cardClass += ' wpov-pq-delivered';
              }

              return (
                <div key={order.id} className={cardClass}>
                  <div className="wpov-pq-item-top">
                    <div className="wpov-pq-item-left">
                      <div className="wpov-pq-item-header">
                        <p className={`wpov-pq-item-table ${order.itemState === 'on-delivery' ? 'wpov-pq-green-text' : ''}`}>Table {order.tableNumber}</p>
                      </div>
                      <div className="wpov-pq-order-id" style={{color:'#9ca3af', fontSize:12, margin:'2px 0 4px 0', textAlign:'left'}}>Order {order.orderNumber || (order.orderIds?.[0] ? String(order.orderIds[0]).substring(0, 8).toUpperCase() : String(order.id).substring(0, 8).toUpperCase())}</div>
                      {/* Per-table Undo moved into Deliver Items modal */}

                      {/* items list moved below to span full card width */}
                    </div>
                    <div className="wpov-pq-item-meta">
                      <div className="wpov-pq-elapsed">
                        <Clock className="wpov-pq-small" />
                        <span className={isUrgent ? 'wpov-pq-urgent-text' : ''} title="Time since order placed">{formatElapsedShort(elapsed)}</span>
                      </div>
                      {/* moved Undo Items to the left column for a cleaner top-right time area */}
                    </div>
                  </div>

                  {/* order meta moved here to span full card width */}
                  <div className="wpov-pq-order-meta">
                    <div className="wpov-pq-stats-grid">
                      <div className="wpov-pq-stat wpov-green">
                        <div className="wpov-pq-stat-label">Ready on Pass</div>
                        <div className="wpov-pq-stat-value">{readyOnPass}</div>
                      </div>
                      <div className="wpov-pq-stat wpov-gray">
                        <div className="wpov-pq-stat-label">Served</div>
                        <div className="wpov-pq-stat-value">{served}</div>
                      </div>
                    </div>
                  </div>

                  {/* items list now spans full card width */}
                  <div className="wpov-pq-items-col">
                    {items.map((orderItem, idx) => {
                      let st = orderItem.status;
                      if (!st || st === 'pending' || st === null) {
                        st = 'ready_on_pass';
                      }
                      return (
                        <div key={idx} className={`wpov-line-item ${st}`}>
                          <div className="wpov-line-left">
                            <div>
                              <span className="wpov-item-name">{orderItem.name || orderItem.item_name}</span>
                              {/* Display customizations if present */}
                              {orderItem.customizations && Array.isArray(orderItem.customizations) && orderItem.customizations.length > 0 && (
                                <div style={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: '4px',
                                  marginTop: '4px'
                                }}>
                                  {orderItem.customizations.map((customization, custIdx) => (
                                    <span key={custIdx} style={{
                                      background: '#2d2d2d',
                                      color: '#fbbf24',
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      fontSize: '10px',
                                      fontWeight: '500',
                                      border: '1px solid #3f3f3f'
                                    }}>
                                      {customization}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <span className="wpov-item-qty">x{orderItem.quantity}</span>
                          </div>
                          <div className={`wpov-status-pill ${st}`}>
                            {String(st).replace(/_/g,' ').toUpperCase()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {order.itemState === 'ready' && (
                    <button onClick={() => setDeliverOrder(order)} className="wpov-pq-confirm">Deliver Items</button>
                  )}
                  {order.itemState === 'on-delivery' && (
                    <button onClick={() => setDeliverOrder(order)} className="wpov-pq-confirm">Deliver Items</button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="wpov-pq-list">
            {loadingHistory ? (
              <div className="wpov-pq-empty">
                <p>Loading order history...</p>
              </div>
            ) : historyError ? (
              <div className="wpov-pq-empty">
                <p style={{color: '#ef4444'}}>Error loading history: {historyError}</p>
              </div>
            ) : servedHistory.length === 0 ? (
              <div className="wpov-pq-empty"><CheckCircle className="wpov-pq-empty-icon" /><p>No history yet</p></div>
            ) : (
              servedHistory
                .sort((a,b)=> new Date(b.servedAt).getTime() - new Date(a.servedAt).getTime())
                .map(h => {
                  const isLastForTicket = servedHistory.every(e => e.orderId !== h.orderId || new Date(e.servedAt).getTime() <= new Date(h.servedAt).getTime());
                  // Merge any duplicate items within this entry (same id/name)
                  const merged = Object.values((h.items || []).reduce((acc, it) => {
                    const key = it.id || it.name;
                    if (!acc[key]) acc[key] = { ...it };
                    else acc[key].quantity += it.quantity || 0;
                    return acc;
                  }, {}));
                  return (
                    <div key={`${h.orderId}-${h.servedAt}`} className="wpov-pq-item">
                      <div className="wpov-pq-item-top">
                        <div className="wpov-pq-item-left">
                          <div className="wpov-pq-item-header">
                            <p className="wpov-pq-item-table">Table {h.tableNumber}</p>
                            <span className="wpov-pq-history-pill">Served</span>
                          </div>
                          <div className="wpov-pq-order-id" style={{color:'#9ca3af', fontSize:12, margin:'2px 0 6px 0', textAlign:'left'}}>Order {h.orderNumber || h.orderId}</div>
                          <div className="wpov-pq-items-col">
                            {merged.map((it, idx) => (
                              <div key={idx} className="wpov-line-item" style={{borderColor:'#2f3237'}}> 
                                <div className="wpov-line-left">
                                  <span className="wpov-item-name">{it.name}</span>
                                  <span className="wpov-item-qty">x{it.quantity}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="wpov-pq-item-meta" style={{textAlign:'right'}}>
                          <div className="wpov-pq-last">{(() => {
                            // ✅ Display time in IST (India Standard Time)
                            const date = new Date(h.servedAt);
                            const istOffset = 5.5 * 60 * 60 * 1000; // 5 hours 30 minutes
                            const istTime = new Date(date.getTime() + istOffset);
                            return istTime.toLocaleTimeString('en-IN', { 
                              hour: '2-digit', 
                              minute: '2-digit',
                              hour12: true,
                              timeZone: 'UTC' // We already added offset, so use UTC to prevent double conversion
                            });
                          })()}</div>
                          {isLastForTicket && canUndoForTicket(h.orderId) && (
                            <div style={{marginTop:6}}>
                              <button
                                className="wpov-history-toggle"
                                onClick={() => {
                                  // Remove just this entry from history
                                  setServedHistory(prev => prev.filter(e => !(e.orderId === h.orderId && new Date(e.servedAt).getTime() === new Date(h.servedAt).getTime())));
                                  // Restore the ticket to its previous state
                                  undoLastDeliveryForTicket(h.orderId);
                                }}
                              >
                                Undo
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
            )}
          </div>
        )}
      </div>
      {deliverOrder && (
        <DeliverItemsModal 
          order={deliverOrder}
          onClose={() => setDeliverOrder(null)}
          onUndoTable={(tableNumber)=>{
            // Remove the last history entry for this table
            setServedHistory(prev => {
              const idx = [...prev].map((h, i) => ({i, t: h.tableNumber})).reverse().find(e => e.t === tableNumber)?.i;
              if (idx === undefined) return prev;
              const copy = [...prev];
              copy.splice(idx, 1);
              return copy;
            });
            undoLastDeliveryForTable(tableNumber);
          }}
          onConfirm={(ids)=>{
            // Start animation for this order
            setRemovingItems(prev => new Set(prev).add(deliverOrder.id));
            
            // Prefer fine-grained selection if provided by parent
            if (onDeliverSelected) {
              onDeliverSelected(deliverOrder.id, ids);
            } else if (onConfirmDelivered) {
              // fallback to full delivery
              onConfirmDelivered(deliverOrder.id);
            }
            setDeliverOrder(null);
            // Log into served history
            setServedHistory(prev => {
              const when = new Date();
              const items = (ids || []).map(({id, quantity}) => {
                const it = (deliverOrder.items || []).find(x => x.id === id) || {};
                return { id, name: it.name || it.item_name || id, quantity };
              });
              // Skip adding empty, and prevent exact duplicate (same orderId + timestamp ms)
              if (!items.length) return prev;
              const key = `${deliverOrder.id}-${when.getTime()}`;
              const exists = prev.some(h => h._k === key);
              if (exists) return prev;
              // attach a hidden key to help avoid dupes
              return [
                ...prev,
                {
                  _k: key,
                  orderId: deliverOrder.id,
                  orderNumber: deliverOrder.orderIds?.[0],
                  tableNumber: deliverOrder.tableNumber,
                  servedAt: when,
                  items
                }
              ];
            });
            
            // Wait for animation to complete, then remove from state
            setTimeout(()=>{
              setDeliveredItems(prev=>prev.filter(i=>i.id!==deliverOrder.id));
              setRemovingItems(prev => {
                const newSet = new Set(prev);
                newSet.delete(deliverOrder.id);
                return newSet;
              });
            }, 500);
          }}
        />
      )}
    </div>
  );
};
